<template>
    <div
        class="price-breakdown-row"
        :class="[
            item.disabled && 'strikethrough',
            hasDetails && 'details',
            mobile && 'mobile',
        ]">
        <div class="price-breakdown-row-content">
            <div v-if="item.title" class="price-breakdown-row-title">
                <span class="mr-2">{{ item.title }}</span>
            </div>

            <div class="price-breakdown-row-text" :class="mobile && 'mobile'">
                <div class="price-breakdown-row-text-wrapper">
                    <div class="price-breakdown-row-text">
                        <span class="price-breakdown-row-label mr-2">
                            {{ item.label }}
                        </span>

                        <span class="price-breakdown-row-sub-label mr-2" v-if="item.subLabel">
                            {{ item.subLabel }}
                        </span>
                    </div>
                    <PricePolicyBadge
                        v-if="pricePolicyProduct"
                        :product="pricePolicyProduct"
                        :isTripSummary="true" />
                    <RebookableBadge v-if="showRebookableBadge" />
                </div>
                <div class="price-breakdown-row-details">
                    <RowDetails v-if="hasDetails && isDesktop && !mobile" :item="item" />
                </div>
            </div>
        </div>

        <div
            class="price-breakdown-row-price"
            :class="[hasDetails && 'details', mobile && 'mobile']">
            <RowDetails v-if="hasDetails && (!isDesktop || mobile)" :item="item" />

            <ProductPricing v-if="productPrice?.oldPrice" v-bind="productPrice" />

            <div v-else class="flex-shrink-0 pl-3 price">
                {{ item.priceIsNegative && '-' }}
                {{ getMoneyObjectAsString(item.price) }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, computed, PropType } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { DETAILS_ALLOWED } from '../consts';
    import { ProductRowItem } from '../types';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { FlightProduct, HotelProduct, ProductType } from '@/api/Cart/cart.interfaces';
    import { getCartProductCancellation } from '../../Cancellation/CartCancellation/CartCancellation.helpers';
    import { getProductPricing } from '@/helpers/price';
    import RowDetails from './RowDetails.vue';
    import PricePolicyBadge from '../../PricePolicyBadge/PricePolicyBadge.vue';
    import RebookableBadge from '@/components/shared/RebookableBadge/RebookableBadge.vue';
    import ProductPricing from '@ui/components/ProductPricing/ProductPricing.vue';

    export default defineComponent({
        name: 'Row',
        components: { ProductPricing, RebookableBadge, RowDetails, PricePolicyBadge },
        props: {
            item: {
                type: Object as PropType<ProductRowItem>,
                required: true,
            },
            mobile: {
                type: Boolean,
            },
        },
        methods: { getMoneyObjectAsString, t },
        setup({ item }) {
            const { isDesktop } = useScreenSize();

            const showRebookableBadge = computed(
                () =>
                    item.product?.Type === ProductType.Flight &&
                    (item.product as FlightProduct).IsRebookable
            );

            const hasDetails = computed(
                () =>
                    !!item.product &&
                    !!item.product?.$type &&
                    DETAILS_ALLOWED.includes(item.product.Type)
            );

            const pricePolicyProduct = computed<HotelProduct | undefined>(() => {
                const product = item.product;
                switch (product?.Type) {
                    case ProductType.Hotel: {
                        const hotel = product as HotelProduct;
                        const isRefundable = getCartProductCancellation(hotel);

                        return isRefundable ? hotel : undefined;
                    }
                    default:
                        return undefined;
                }
            });

            const productPrice = computed(() =>
                item.product ? getProductPricing(item.product) : undefined
            );

            return {
                isDesktop,
                pricePolicyProduct,
                showRebookableBadge,
                hasDetails,
                productPrice,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'PriceBreakdown';
</style>
